import { useEffect, useState } from 'react';
import lodash from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { Notifications } from '../../Notifications';
import i18n from 'i18next';
import ItemSearchBar from './filters/itemSearchBar';
import PresetsSidebar from './filters/PresetsSidebar';
import CreatePresetDialog from './filters/CreatePresetDialog';
import { PresetEntity } from '../../../services/PresetService';
import ItemFilters from './filters/ItemFilters';
import { useStores } from '../../../hooks';
import { FEATURES } from '@enterprise/core';
import { GetItemQuery, InvoiceItem } from '../../../core/models';

interface PageProps {
    searchItems: any;
    searchItemsForAutoComplete?: (query: GetItemQuery) => Promise<InvoiceItem[]>;
}

const CLASSIFICATION_TRIGGERS = ['class: ', 'class:'];

export const ItemSearchPanel = observer(function ItemSearchPanelComponent(props: PageProps) {
    // Access nested stores lazily, as the class getters did.
    const { domain, ui } = useStores();
    const itemsPageStore = domain.itemsPageStore;
    const constantsStore = domain.constantsStore;
    const appStore = ui?.app;

    const [currentFilter, setCurrentFilter] = useState<any>({ searchValue: '' });
    const [selectedPresetId, setSelectedPresetId] = useState<any>(undefined);
    const [isShowPresetsSidebar, setIsShowPresetsSidebar] = useState(false);
    const [isShowCreatePresetModal, setIsShowCreatePresetModal] = useState(false);

    useEffect(() => {
        setCurrentFilter(itemsPageStore.filter || { searchValue: '' });
    }, [itemsPageStore]);

    const enhancedSearchEnabled = appStore.featureManager.isAvailable(FEATURES.ENHANCED_PRODUCT_SEARCH, true);

    const isValidSearch = (value, type) => {
        const valueLength = value.trim().length;

        let validationError;
        if (valueLength === 0) {
            validationError = i18n.t('validation:searchNotEmpty', {
                field: i18n.t('controlCenter:itemSearch.id', 'ID'),
            });
        }

        if (validationError) {
            Notifications.warn(validationError);
            return false;
        }
        return true;
    };

    const getSearchValueParams = (filter, value: string = filter.searchValue): {} => {
        const { searchType = itemsPageStore.itemsGroupBy } = filter;
        return searchType === 'id' ? { id: value } : { description: value };
    };

    // `filterOverride` replaces the old `setState(..., callback)` sequencing: callers that update
    // the filter and immediately search pass the new filter rather than relying on state timing.
    const searchItems = (filterOverride?: any) => {
        // On intial load, the type is not available to the filter. We check it here to see if it is set or not, and not
        // expicity set it. This will likely only happen on the first search after a refresh of the page.
        const searchFilter = filterOverride ?? currentFilter;
        const searchQuery = { searchType: searchFilter.searchType, searchValue: searchFilter.searchValue };

        const trigger = CLASSIFICATION_TRIGGERS.find((item) => {
            if (searchFilter.searchValue?.toLowerCase().includes(item)) {
                return item;
            }
            return undefined;
        });
        if (searchFilter.searchValue.toLowerCase().includes(trigger) && enhancedSearchEnabled) {
            searchQuery.searchValue = searchFilter.searchValue.substring(trigger?.length);
            searchQuery.searchType = 'classification';
        }

        if (!searchFilter.searchType && !searchQuery.searchType) {
            setCurrentFilter((previous) => ({ ...previous, searchType: itemsPageStore.itemsGroupBy }));
            searchFilter.searchType = itemsPageStore.itemsGroupBy;
        }

        const { searchType = itemsPageStore.itemsGroupBy, searchValue, classId } = searchFilter;
        if (!classId && !isValidSearch(searchValue, searchType)) {
            return;
        }

        const query = {
            ...lodash.mapValues(enhancedSearchEnabled ? searchQuery : searchFilter, (val) => (!lodash.isNil(val) ? val.toString() : null)),
            ...getSearchValueParams(searchFilter),
        };

        props.searchItems(query);
    };

    const updateFilter = (update) => {
        const newFilter = {
            ...currentFilter,
            ...update,
        };
        setCurrentFilter(newFilter);
        itemsPageStore.filter = newFilter;
        return newFilter;
    };

    const updateFilterAndSearch = (update) => {
        const newFilter = updateFilter(update);
        searchItems(newFilter);
    };

    const showCreatePresetModal = () => {
        setIsShowCreatePresetModal((previous) => !previous);
    };

    const savePreset = (presetName) => {
        const newPreset = new PresetEntity();
        newPreset.name = presetName;
        newPreset.content = currentFilter;
        itemsPageStore.savePreset(newPreset).then(showCreatePresetModal);
    };

    const applyPreset = (presetId) => {
        const preset = itemsPageStore.presets.find((p) => p.id === presetId);
        if (preset) {
            setSelectedPresetId(presetId);
            setCurrentFilter(preset.content);
        }
    };

    const showPresetsSidebar = () => {
        setIsShowPresetsSidebar((previous) => !previous);
    };

    const { searchType = itemsPageStore.itemsGroupBy, classId = '', ...rest } = currentFilter;

    const filterForChildren = {
        ...rest,
        searchType,
        classId,
    };

    return (
        <>
            {isShowPresetsSidebar && <PresetsSidebar close={showPresetsSidebar} applyPreset={applyPreset} />}

            {isShowCreatePresetModal && <CreatePresetDialog isVisible={true} onSubmit={savePreset} onClose={showCreatePresetModal} />}

            <Row>
                <Col xs={12}>
                    <ItemSearchBar
                        classifications={itemsPageStore.getLookupResults(itemsPageStore.classifications)}
                        presets={itemsPageStore.presets}
                        submit={() => searchItems()}
                        currentFilter={filterForChildren}
                        updateFilter={updateFilter}
                        selectedPresetId={selectedPresetId}
                        applyPreset={applyPreset}
                        viewAllPresets={showPresetsSidebar}
                        searchItemsForAutoComplete={props.searchItemsForAutoComplete}
                    />
                </Col>
            </Row>
            <Row style={{ marginTop: '-20px' }}>
                <Col xs={12}>
                    <ItemFilters
                        applications={constantsStore.applications}
                        itemTypes={itemsPageStore.getLookupResults(itemsPageStore.itemTypes)}
                        currentFilter={filterForChildren}
                        updateFilter={updateFilterAndSearch}
                        savePreset={showCreatePresetModal}
                        enhancedSearch={enhancedSearchEnabled}
                    />
                </Col>
            </Row>
        </>
    );
});
